import {z} from 'zod';

import {TimeRangeSchema} from '@dms/api/base';

import {TELEPHONY_CALL_NOTE_MAX_LENGTH} from './TELEPHONY_CALL_NOTE_MAX_LENGTH';

export const GetTwilioAccountApiResponseSchema = z.object({
  twilioAccountSid: z.string().nullable(),
  friendlyName: z.string().nullable(),
  status: z.string().nullable(),
});

export type GetTwilioAccountApiResponse = z.infer<typeof GetTwilioAccountApiResponseSchema>;

/**
 * Freshness of the live Twilio read for an attached number: 'missing' means the sid is gone from
 * the tenant subaccount (configuration drift), 'unavailable' only that Twilio was unreachable.
 */
export const TwilioNumberLookupStatusSchema = z.enum(['ok', 'missing', 'unavailable']);

export type TwilioNumberLookupStatus = z.infer<typeof TwilioNumberLookupStatusSchema>;

/**
 * `lastInboundCallAt` is null until the first inbound call reaches the number through Omnetic; a
 * number without one is attached but unproven, so the line must not read as active.
 */
export const TelephonyLineTwilioNumberSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  lookupStatus: TwilioNumberLookupStatusSchema,
  lastInboundCallAt: z.string().nullable(),
});

export type TelephonyLineTwilioNumber = z.infer<typeof TelephonyLineTwilioNumberSchema>;

/**
 * Telephony's own weekly schedule. Same wire shape as the branch opening hours today, but a
 * different backend object (`TelephonyLineBusinessHours*`), so it is not shared with them and may
 * drift on its own. A missing or empty day is closed; a `null` schedule means always open.
 */
export const TelephonyLineBusinessHoursSchema = z.object({
  monday: z.array(TimeRangeSchema),
  tuesday: z.array(TimeRangeSchema),
  wednesday: z.array(TimeRangeSchema),
  thursday: z.array(TimeRangeSchema),
  friday: z.array(TimeRangeSchema),
  saturday: z.array(TimeRangeSchema),
  sunday: z.array(TimeRangeSchema),
});

export type TelephonyLineBusinessHours = z.infer<typeof TelephonyLineBusinessHoursSchema>;

export const TelephonyLineRecordingConsentSchema = z.object({
  approved: z.boolean(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
});

export type TelephonyLineRecordingConsent = z.infer<typeof TelephonyLineRecordingConsentSchema>;

export const TelephonyRoutingTargetKindSchema = z.enum(['group', 'user']);

export type TelephonyRoutingTargetKind = z.infer<typeof TelephonyRoutingTargetKindSchema>;

/** Who rings when the line is open: a telephony group or a single user, identified by `id`. */
export const TelephonyRoutingTargetSchema = z.object({
  kind: TelephonyRoutingTargetKindSchema,
  id: z.string(),
});

export type TelephonyRoutingTarget = z.infer<typeof TelephonyRoutingTargetSchema>;

export const TelephonyFallbackKindSchema = z.enum(['message', 'bot']);

export type TelephonyFallbackKind = z.infer<typeof TelephonyFallbackKindSchema>;

/**
 * What a caller gets when nobody can take the call: for `message` the text is read out and the call
 * ends; for `bot` the caller is handed to the voice bot, and the text is read when the bot cannot be
 * reached.
 */
export const TelephonyFallbackSchema = z.object({
  kind: TelephonyFallbackKindSchema,
  message: z.string().nullable(),
});

export type TelephonyFallback = z.infer<typeof TelephonyFallbackSchema>;

/**
 * With `isDefault` the rule fields are null: everyone eligible on the line's branch rings at once
 * and built-in messages cover the closed and unanswered cases.
 */
export const TelephonyLineRoutingSchema = z.object({
  isDefault: z.boolean(),
  openTarget: TelephonyRoutingTargetSchema.nullable(),
  closedFallback: TelephonyFallbackSchema.nullable(),
  noAnswerFallback: TelephonyFallbackSchema.nullable(),
});

export type TelephonyLineRouting = z.infer<typeof TelephonyLineRoutingSchema>;

export const TelephonyLineSchema = z.object({
  id: z.string(),
  name: z.string(),
  branchId: z.string().nullable(),
  businessHours: TelephonyLineBusinessHoursSchema.nullable(),
  greetingIntro: z.string().nullable(),
  greetingOutOfHours: z.string().nullable(),
  recordingConsent: TelephonyLineRecordingConsentSchema,
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
  twilioNumbers: z.array(TelephonyLineTwilioNumberSchema),
  routing: TelephonyLineRoutingSchema,
});

export type TelephonyLine = z.infer<typeof TelephonyLineSchema>;

export const GetTelephonyLinesApiResponseSchema = z.object({
  lines: z.array(TelephonyLineSchema),
});

export type GetTelephonyLinesApiResponse = z.infer<typeof GetTelephonyLinesApiResponseSchema>;

export const GetTelephonyLineApiArgSchema = z.object({
  lineId: z.string(),
});

export type GetTelephonyLineApiArg = z.infer<typeof GetTelephonyLineApiArgSchema>;

export type GetTelephonyLineApiResponse = TelephonyLine;

export const CreateTelephonyLineRequestBodySchema = z.object({
  name: z.string(),
});

export type CreateTelephonyLineRequestBody = z.infer<typeof CreateTelephonyLineRequestBodySchema>;

export const CreateTelephonyLineApiArgSchema = z.object({
  body: CreateTelephonyLineRequestBodySchema,
});

export type CreateTelephonyLineApiArg = z.infer<typeof CreateTelephonyLineApiArgSchema>;

export const CreateTelephonyLineApiResponseSchema = z.object({
  id: z.string(),
});

export type CreateTelephonyLineApiResponse = z.infer<typeof CreateTelephonyLineApiResponseSchema>;

/**
 * Full replacement of the line configuration: every key is sent on every save, `null` clears it.
 */
export const UpdateTelephonyLineRequestBodySchema = z.object({
  name: z.string(),
  branchId: z.string().nullable(),
  businessHours: TelephonyLineBusinessHoursSchema.nullable(),
  greetingIntro: z.string().nullable(),
  greetingOutOfHours: z.string().nullable(),
});

export type UpdateTelephonyLineRequestBody = z.infer<typeof UpdateTelephonyLineRequestBodySchema>;

export const UpdateTelephonyLineApiArgSchema = z.object({
  lineId: z.string(),
  body: UpdateTelephonyLineRequestBodySchema,
});

export type UpdateTelephonyLineApiArg = z.infer<typeof UpdateTelephonyLineApiArgSchema>;

export const DeleteTelephonyLineApiArgSchema = z.object({
  lineId: z.string(),
});

export type DeleteTelephonyLineApiArg = z.infer<typeof DeleteTelephonyLineApiArgSchema>;

export const AttachTelephonyLineNumberApiArgSchema = z.object({
  lineId: z.string(),
  numberSid: z.string(),
});

export type AttachTelephonyLineNumberApiArg = z.infer<typeof AttachTelephonyLineNumberApiArgSchema>;

export const DetachTelephonyLineNumberApiArgSchema = z.object({
  lineId: z.string(),
  numberSid: z.string(),
});

export type DetachTelephonyLineNumberApiArg = z.infer<typeof DetachTelephonyLineNumberApiArgSchema>;

export const TelephonyLineRecordingConsentApiArgSchema = z.object({
  lineId: z.string(),
});

export type TelephonyLineRecordingConsentApiArg = z.infer<
  typeof TelephonyLineRecordingConsentApiArgSchema
>;

export const TwilioNumberSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  lineId: z.string().nullable(),
});

export type TwilioNumber = z.infer<typeof TwilioNumberSchema>;

export const GetTwilioNumbersApiResponseSchema = z.object({
  numbers: z.array(TwilioNumberSchema),
});

export type GetTwilioNumbersApiResponse = z.infer<typeof GetTwilioNumbersApiResponseSchema>;

export const TelephonyFallbackRequestBodySchema = z.object({
  kind: TelephonyFallbackKindSchema,
  message: z.string(),
});

export type TelephonyFallbackRequestBody = z.infer<typeof TelephonyFallbackRequestBodySchema>;

/**
 * Full replacement of the line's routing rule; `resetTelephonyLineRouting` puts the line back on
 * the default routing.
 */
export const ConfigureTelephonyLineRoutingRequestBodySchema = z.object({
  openTarget: TelephonyRoutingTargetSchema,
  closedFallback: TelephonyFallbackRequestBodySchema,
  noAnswerFallback: TelephonyFallbackRequestBodySchema,
});

export type ConfigureTelephonyLineRoutingRequestBody = z.infer<
  typeof ConfigureTelephonyLineRoutingRequestBodySchema
>;

export const ConfigureTelephonyLineRoutingApiArgSchema = z.object({
  lineId: z.string(),
  body: ConfigureTelephonyLineRoutingRequestBodySchema,
});

export type ConfigureTelephonyLineRoutingApiArg = z.infer<
  typeof ConfigureTelephonyLineRoutingApiArgSchema
>;

export const ResetTelephonyLineRoutingApiArgSchema = z.object({
  lineId: z.string(),
});

export type ResetTelephonyLineRoutingApiArg = z.infer<typeof ResetTelephonyLineRoutingApiArgSchema>;

/**
 * How the members ring: `simultaneous` = everyone at once, first to pick up takes the call;
 * `sequential` = one after another in list order, each for `ringSeconds`.
 */
export const TelephonyGroupRingStrategySchema = z.enum(['simultaneous', 'sequential']);

export type TelephonyGroupRingStrategy = z.infer<typeof TelephonyGroupRingStrategySchema>;

export const TelephonyGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  ringStrategy: TelephonyGroupRingStrategySchema,
  ringSeconds: z.number(),
  memberUserIds: z.array(z.string()),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type TelephonyGroup = z.infer<typeof TelephonyGroupSchema>;

export const GetTelephonyGroupsApiResponseSchema = z.object({
  groups: z.array(TelephonyGroupSchema),
});

export type GetTelephonyGroupsApiResponse = z.infer<typeof GetTelephonyGroupsApiResponseSchema>;

export const GetTelephonyGroupApiArgSchema = z.object({
  groupId: z.string(),
});

export type GetTelephonyGroupApiArg = z.infer<typeof GetTelephonyGroupApiArgSchema>;

export type GetTelephonyGroupApiResponse = TelephonyGroup;

/** Full replacement of the group: every key is sent on every save, the member list replaces the current one. */
export const TelephonyGroupRequestBodySchema = z.object({
  name: z.string(),
  ringStrategy: TelephonyGroupRingStrategySchema,
  ringSeconds: z.number(),
  memberUserIds: z.array(z.string()),
});

export type TelephonyGroupRequestBody = z.infer<typeof TelephonyGroupRequestBodySchema>;

export const CreateTelephonyGroupApiArgSchema = z.object({
  body: TelephonyGroupRequestBodySchema,
});

export type CreateTelephonyGroupApiArg = z.infer<typeof CreateTelephonyGroupApiArgSchema>;

export const CreateTelephonyGroupApiResponseSchema = z.object({
  id: z.string(),
});

export type CreateTelephonyGroupApiResponse = z.infer<typeof CreateTelephonyGroupApiResponseSchema>;

export const UpdateTelephonyGroupApiArgSchema = z.object({
  groupId: z.string(),
  body: TelephonyGroupRequestBodySchema,
});

export type UpdateTelephonyGroupApiArg = z.infer<typeof UpdateTelephonyGroupApiArgSchema>;

export const DeleteTelephonyGroupApiArgSchema = z.object({
  groupId: z.string(),
});

export type DeleteTelephonyGroupApiArg = z.infer<typeof DeleteTelephonyGroupApiArgSchema>;

/** A line the caller can dial out from: only lines with a number attached are ever listed. */
export const TelephonyCallLineSchema = z.object({
  id: z.string(),
  name: z.string(),
  phoneNumber: z.string(),
});

export type TelephonyCallLine = z.infer<typeof TelephonyCallLineSchema>;

/** A line offered in the Call dialog; `branchId` is null for a line that belongs to no branch. */
export const TelephonyCallOptionLineSchema = TelephonyCallLineSchema.extend({
  branchId: z.string().nullable(),
});

export type TelephonyCallOptionLine = z.infer<typeof TelephonyCallOptionLineSchema>;

/**
 * Everything the Call dialog needs before the call starts. `userPhoneNumber` null means the
 * caller has no mobile in their profile. `branchLine` is the line of the branch the request was
 * made for (`X-Branch`) and only a preselection: any of `lines` (every tenant line with a number)
 * can be picked. An empty `lines` means nobody on the tenant can call yet.
 */
export const GetTelephonyCallOptionsApiResponseSchema = z.object({
  userPhoneNumber: z.string().nullable(),
  branchLine: TelephonyCallOptionLineSchema.nullable(),
  lines: z.array(TelephonyCallOptionLineSchema),
});

export type GetTelephonyCallOptionsApiResponse = z.infer<
  typeof GetTelephonyCallOptionsApiResponseSchema
>;

/**
 * Lifecycle of an outbound call: Omnetic rings the caller's mobile, the caller presses 1, the
 * customer is dialled. `ended` is terminal and carries the `outcome`.
 */
export const TelephonyOutboundCallStatusSchema = z.enum([
  'ringing_user',
  'waiting_for_confirmation',
  'connecting_customer',
  'in_call',
  'ended',
]);

export const TelephonyOutboundCallOutcomeSchema = z.enum([
  'connected',
  'customer_not_reached',
  'not_confirmed',
  'user_not_reached',
]);

/**
 * Lifecycle of an inbound call as the viewer sees it: `ringing` while the viewer is rung in the
 * current step, `in_call` only for the user who answered. Everybody else reads `ended` at once:
 * `answered` (a colleague took it), `passed_on` (the call moved to the next ring step).
 */
export const TelephonyInboundCallStatusSchema = z.enum(['ringing', 'in_call', 'ended']);

export const TelephonyInboundCallOutcomeSchema = z.enum([
  'answered',
  'nobody_answered',
  'caller_hung_up',
  'passed_on',
]);

/**
 * Where the caller talks on an outbound call: `mobile` rings the caller's phone first, `browser`
 * connects the Omnetic tab through the Twilio Voice SDK and skips the caller-leg states.
 */
export const TelephonyCallChannelSchema = z.enum(['mobile', 'browser']);

export type TelephonyCallChannel = z.infer<typeof TelephonyCallChannelSchema>;

export const TelephonyCallAnsweredBySchema = z.object({
  id: z.string(),
  name: z.string(),
});

/** A record the call is paired with, by hand during or after the call. */
export const TelephonyCallRecordSchema = z.object({
  resourceId: z.string(),
  recordId: z.string(),
});

export type TelephonyCallRecord = z.infer<typeof TelephonyCallRecordSchema>;

const TelephonyCallBaseSchema = z.object({
  id: z.string(),
  customerName: z.string().nullable(),
  line: TelephonyCallLineSchema,
  startedAt: z.string(),
  answeredAt: z.string().nullable(),
  connectedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  answeredBy: TelephonyCallAnsweredBySchema.nullable(),
  /**
   * Sorted by `resourceId`, then `recordId`; the same before and after the call end. A backend
   * without call pairing leaves it out: no records.
   */
  records: z
    .array(TelephonyCallRecordSchema)
    .nullish()
    .transform((records) => records ?? []),
  /**
   * The user's note on the call; written live and carried into the stored call at the end. A
   * backend without call notes leaves it out: no note.
   */
  note: z
    .string()
    .nullish()
    .transform((note) => note ?? null),
});

export const TelephonyOutboundCallSchema = TelephonyCallBaseSchema.extend({
  direction: z.literal('outbound'),
  channel: TelephonyCallChannelSchema,
  status: TelephonyOutboundCallStatusSchema,
  outcome: TelephonyOutboundCallOutcomeSchema.nullable(),
  customerId: z.string(),
  /** The dialled number. */
  customerPhoneNumber: z.string(),
});

/**
 * `customerId` is null for a caller whose number matches no customer, `customerPhoneNumber` for a
 * caller who withheld the number.
 */
export const TelephonyInboundCallSchema = TelephonyCallBaseSchema.extend({
  direction: z.literal('inbound'),
  /** An inbound call rings the mobile and the browser together, so it has no channel. */
  channel: z.null(),
  status: TelephonyInboundCallStatusSchema,
  outcome: TelephonyInboundCallOutcomeSchema.nullable(),
  customerId: z.string().nullable(),
  customerPhoneNumber: z.string().nullable(),
});

export const TelephonyCallSchema = z.discriminatedUnion('direction', [
  TelephonyOutboundCallSchema,
  TelephonyInboundCallSchema,
]);

export type TelephonyCall = z.infer<typeof TelephonyCallSchema>;

export type TelephonyOutboundCall = z.infer<typeof TelephonyOutboundCallSchema>;

export type TelephonyInboundCall = z.infer<typeof TelephonyInboundCallSchema>;

export type TelephonyCallStatus = TelephonyCall['status'];

/**
 * Payload of the `telephony-outbound_call_state_changed` and `telephony-inbound_call_state_changed`
 * background notifications: the call as the recipient sees it, keyed by `callId`.
 */
export const TelephonyCallStateChangedPayloadSchema = z.discriminatedUnion('direction', [
  TelephonyOutboundCallSchema.omit({id: true}).extend({callId: z.string()}),
  TelephonyInboundCallSchema.omit({id: true}).extend({callId: z.string()}),
]);

export type TelephonyCallStateChangedPayload = z.infer<
  typeof TelephonyCallStateChangedPayloadSchema
>;

export type GetTelephonyCurrentCallApiResponse = TelephonyCall | null;

export const GetTelephonyCallApiArgSchema = z.object({
  callId: z.string(),
});

export type GetTelephonyCallApiArg = z.infer<typeof GetTelephonyCallApiArgSchema>;

export type GetTelephonyCallApiResponse = TelephonyCall;

/**
 * `lineId` is sent only when the caller's branch has no line and the caller picked one. A
 * `browser` call only creates the call record; the tab then connects it through the Voice SDK.
 */
export const StartTelephonyCallRequestBodySchema = z.object({
  customerId: z.string(),
  phoneNumber: z.string(),
  lineId: z.string().nullable(),
  channel: TelephonyCallChannelSchema,
});

export type StartTelephonyCallRequestBody = z.infer<typeof StartTelephonyCallRequestBodySchema>;

export const StartTelephonyCallApiArgSchema = z.object({
  body: StartTelephonyCallRequestBodySchema,
});

export type StartTelephonyCallApiArg = z.infer<typeof StartTelephonyCallApiArgSchema>;

export const StartTelephonyCallApiResponseSchema = z.object({
  id: z.string(),
});

export type StartTelephonyCallApiResponse = z.infer<typeof StartTelephonyCallApiResponseSchema>;

export const CancelTelephonyCallApiArgSchema = z.object({
  callId: z.string(),
});

export type CancelTelephonyCallApiArg = z.infer<typeof CancelTelephonyCallApiArgSchema>;

/**
 * Pairs a call with an interest, business case, service case or workshop interest, live or ended.
 * Idempotent; a call without a customer takes the record's customer on its first pairing.
 */
export const AssignTelephonyCallRecordApiArgSchema = z.object({
  callId: z.string(),
  body: TelephonyCallRecordSchema,
});

export type AssignTelephonyCallRecordApiArg = z.infer<typeof AssignTelephonyCallRecordApiArgSchema>;

/** `null` or an empty string clears the note. */
export const SetTelephonyCallNoteApiArgSchema = z.object({
  callId: z.string(),
  body: z.object({
    note: z.string().max(TELEPHONY_CALL_NOTE_MAX_LENGTH).nullable(),
  }),
});

export type SetTelephonyCallNoteApiArg = z.infer<typeof SetTelephonyCallNoteApiArgSchema>;

export const UnassignTelephonyCallRecordApiArgSchema = TelephonyCallRecordSchema.extend({
  callId: z.string(),
});

export type UnassignTelephonyCallRecordApiArg = z.infer<
  typeof UnassignTelephonyCallRecordApiArgSchema
>;

/**
 * A short-lived Twilio Access Token for the Voice SDK `Device`. `identity` is the Twilio client
 * name the token was issued for; the frontend passes the token on and never reads the identity.
 */
export const GetTelephonyVoiceTokenApiResponseSchema = z.object({
  token: z.string(),
  identity: z.string(),
  expiresAt: z.string(),
});

export type GetTelephonyVoiceTokenApiResponse = z.infer<
  typeof GetTelephonyVoiceTokenApiResponseSchema
>;

/** Direction of a stored call, as the detail page and the call list name it. */
export const TelephonyStoredCallDirectionSchema = z.enum(['inbound', 'outbound']);

export type TelephonyStoredCallDirection = z.infer<typeof TelephonyStoredCallDirectionSchema>;

/**
 * How a stored call ended; the same vocabulary as the customer thread's call rows. `connected`
 * is the only outcome with a conversation; `bot` means the voice bot took the call.
 */
export const TelephonyStoredCallOutcomeSchema = z.enum([
  'connected',
  'nobody_answered',
  'caller_hung_up',
  'customer_not_reached',
  'bot',
]);

export type TelephonyStoredCallOutcome = z.infer<typeof TelephonyStoredCallOutcomeSchema>;

const InterestRecordDetailSchema = z.object({
  /** An interest may have no public id yet. */
  publicId: z.string().nullable(),
  state: z.enum(['NEW', 'OPEN', 'CLOSED', 'UNSUCCESSFUL']),
  type: z.enum(['SELLING', 'BUYING', 'SWAP']).nullable(),
});

const BusinessCaseRecordDetailSchema = z.object({
  publicId: z.string(),
  state: z.enum([
    'CONCEPT',
    'OFFER',
    'CONTRACT',
    'DEPOSIT',
    'DEPOSIT_PAID',
    'INVOICE',
    'PAID',
    'CLOSED',
    'UNSUCCESSFUL',
  ]),
  type: z.enum(['SELLING', 'BUYING', 'SWAP']).nullable(),
});

const WorkshopInterestRecordDetailSchema = z.object({
  publicId: z.string(),
  state: z.enum(['NEW', 'OPEN', 'CLOSED', 'UNSUCCESSFUL']),
  vehicleTitle: z.string().nullable(),
});

const ServiceCaseRecordDetailSchema = z.object({
  number: z.string(),
  vehicleTitle: z.string().nullable(),
});

/**
 * A record a stored call is linked to, with what the chip shows about it. `detail` is null when
 * the viewer may not read the record or it is gone: the chip then names only the kind.
 */
export const TelephonyCallDetailRecordSchema = z.discriminatedUnion('resourceId', [
  z.object({
    resourceId: z.literal('INTEREST'),
    recordId: z.string(),
    detail: InterestRecordDetailSchema.nullable(),
  }),
  z.object({
    resourceId: z.literal('BUSINESS_CASE'),
    recordId: z.string(),
    detail: BusinessCaseRecordDetailSchema.nullable(),
  }),
  z.object({
    resourceId: z.literal('SERVICE_CASE'),
    recordId: z.string(),
    detail: ServiceCaseRecordDetailSchema.nullable(),
  }),
  z.object({
    resourceId: z.literal('WORKSHOP_INTEREST'),
    recordId: z.string(),
    detail: WorkshopInterestRecordDetailSchema.nullable(),
  }),
]);

export type TelephonyCallDetailRecord = z.infer<typeof TelephonyCallDetailRecordSchema>;

/**
 * A stored call as the call detail page reads it: durable data only, no Twilio check. 404 when the
 * call is not stored yet (live) or never was (an outbound call that never dialled the customer).
 */
export const TelephonyCallDetailSchema = z.object({
  id: z.string(),
  direction: TelephonyStoredCallDirectionSchema,
  outcome: TelephonyStoredCallOutcomeSchema,
  /** The customer's number; null when an inbound caller withheld it. */
  phoneNumber: z.string().nullable(),
  line: z.object({
    id: z.string(),
    name: z.string(),
    phoneNumber: z.string(),
  }),
  /** Who answered an inbound call or placed an outbound one; null when nobody answered. */
  user: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  startedAt: z.string(),
  answeredAt: z.string().nullable(),
  endedAt: z.string(),
  durationSeconds: z.number().nullable(),
  customer: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
  note: z.string().nullable(),
  /** Sorted by `resourceId`, then `recordId`. */
  records: z.array(TelephonyCallDetailRecordSchema),
});

export type TelephonyCallDetail = z.infer<typeof TelephonyCallDetailSchema>;

export const GetTelephonyCallDetailApiArgSchema = z.object({
  callId: z.string(),
});

export type GetTelephonyCallDetailApiArg = z.infer<typeof GetTelephonyCallDetailApiArgSchema>;

export type GetTelephonyCallDetailApiResponse = TelephonyCallDetail;
